/**
 * Everything that belongs to the person who was signed in, cleared in one place.
 *
 * The console keeps several things in `localStorage` so they survive a reload. What it did NOT
 * do was clear them on sign-out: `logout()` removed the auth and tenant entries and left the
 * rest, so the next account to sign in on the same browser inherited them. On a shared machine
 * that is one person reading another's data — the notification bell in particular spells out
 * amounts and balances ("Đã cộng 500.000 ₫… Số dư hiện tại: 2.500.000 ₫").
 *
 * Clearing the browser key alone is not enough, and that is the part worth remembering: the
 * Zustand store is still populated in memory, and `persist` writes it straight back on the next
 * state change. Signing in as someone else without a page reload would restore everything. So
 * each store is reset through its own action, and `persist` then rewrites an empty one.
 *
 * DEVICE settings are deliberately left alone — theme and the resource list's view mode belong
 * to the browser, not to the account, and wiping them would make every sign-out feel like a
 * fresh install.
 */

import { clearDepositDraft } from "@/lib/depositDraft";
import { EXPIRY_NOTICE_STORAGE_KEY } from "@/lib/expiryNoticeDismissal";
import { queryClient } from "@/lib/queryClient";
import { socketIOManager } from "@/lib/socketio";
import { clearSharedUsage } from "@/lib/vmUsageCache";
import { useNotificationStore } from "./notificationStore";
import { useStorageWizardStore } from "./storageWizardStore";
import { useVMWizardStore } from "./vmWizardStore";

/**
 * Plain keys with no store behind them, so removal is the whole job.
 * `lastTenantId` is read on the next OAuth sign-in to preselect a tenant; left behind it is the
 * PREVIOUS account's choice being applied to a new one.
 */
const SESSION_KEYS = [
  "lastTenantId",
  // The VM expiry notices this person dismissed. Not a device setting: the map is keyed by
  // resource id and holds each machine's expiry date, so leaving it behind hands the next account
  // on this browser a list of the previous one's machines and when their terms end.
  EXPIRY_NOTICE_STORAGE_KEY,
] as const;

export function clearSessionData(): void {
  // Reset the store, not just its key: `persist` mirrors in-memory state back to localStorage,
  // so removing the key while the store still holds the data only delays the leak by one write.
  useNotificationStore.getState().clearAll();

  // A half-finished object-storage order — it persists the draft's pricing AND its payment
  // reference, so rehydrating it for the next account shows them someone else's order.
  useStorageWizardStore.getState().resetWizard();

  // The VM wizard keeps the same things under `vm-wizard-draft` — orderId, orderNumber, total,
  // paymentId, paymentOrderNumber and paymentDetails — plus `currentStep`, and it was missed here.
  // The next account to sign in on this browser resumed the previous one's checkout: the wizard
  // opened straight on step 4 and asked the API for THEIR order, which came back
  // `GET_ORDER_FAILED — Order does not belong to current tenant`. The 400 is the visible half; the
  // half that matters is that the previous person's order number, total and bank reference were
  // rehydrated onto someone else's screen.
  useVMWizardStore.getState().resetWizard();

  // A deposit the previous person had started. It lives in `sessionStorage`, which survives a
  // sign-out in the same tab, and it carries their `WIC-PAY-…` reference — so leaving it behind
  // showed the NEXT person that reference on the bank panel, under an instruction to quote it in
  // their transfer. Their money would then reconcile against someone else's payment.
  clearDepositDraft();

  for (const key of SESSION_KEYS) {
    localStorage.removeItem(key);
  }

  // The VM list's cached CPU/RAM readings, keyed by resource id. A machine can change hands
  // (ownership transfer), so the next account must not see the previous one's reading.
  clearSharedUsage();

  // Every API answer this person received. Query keys carry no user or tenant (`["orders",
  // "summary"]`), the cache outlives a sign-out by 10 minutes, and `refetchOnMount: "always"`
  // still renders the cached copy while it refetches — so the next account to sign in on this tab
  // saw the previous one's orders and balances until their own arrived.
  queryClient.clear();

  // The live-update socket, still authenticated as this person. The only disconnect sat in an
  // effect of components the route guard had already unmounted, so it never ran — and
  // `initialize()` reuses an open socket, so the next account to sign in on this tab was handed
  // the previous one's notifications, amounts and balances included. It never throws.
  void socketIOManager.disconnect();
}
